import { FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { IS_ALPHA_NUMERIC } from 'utils/validationRegex';
import { z } from 'zod';
import { AnalyticsProvider, AnalyticsRegion, ThirdPartyIntegration } from '../types';
import {
  ENABLED,
  ID,
  PROJECT_ID,
  API_TOKEN,
  ANALYTICS_PROVIDER,
  ANALYTICS_REGION,
} from '../constants';

const createSchema = z.object({
  [API_TOKEN]: z.string().min(1, { message: 'Enter a write key' }).regex(IS_ALPHA_NUMERIC, {
    message: 'API token must be alphanumeric',
  }),
  /**
   * Currently the only Third Party Integration configured in this page/module is Segment.
   * */
  [ANALYTICS_PROVIDER]: z.enum([AnalyticsProvider.SEGMENT]),
  [ENABLED]: z.boolean(),
  [ANALYTICS_REGION]: z.enum([AnalyticsRegion.US, AnalyticsRegion.EU]),
});

const editSchema = createSchema.extend({
  [ID]: z.number(),
  [PROJECT_ID]: z.number(),
});

const emptyValues = {
  [API_TOKEN]: '',
  /**
   * Currently the only Third Party Integration configured in this page/module is Segment.
   * */
  [ANALYTICS_PROVIDER]: AnalyticsProvider.SEGMENT,
  [ENABLED]: true,
  [ANALYTICS_REGION]: AnalyticsRegion.US,
};

export type ThirdPartyIntegrationFormSchema = Required<
  z.infer<typeof createSchema | typeof editSchema>
>;

type ThirdPartyIntegrationFormProviderProps = {
  initialValues?: ThirdPartyIntegration;
};

export const ThirdPartyIntegrationFormProvider: FC<
  React.PropsWithChildren<ThirdPartyIntegrationFormProviderProps>
> = ({ initialValues, children }) => {
  const schema = initialValues?.[ID] ? editSchema : createSchema;
  const defaultValues = initialValues?.[ID] ? initialValues : emptyValues;

  const methods = useForm<ThirdPartyIntegrationFormSchema>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};
